import type {
  ItemCustoQuery, ItemCustoResponse,
  CenariosDto, CenariosResponse, FormacaoCalculadaResponse, FormacaoResponse, ListaPrecosQuery, ListaPrecosResponse, PorRegimeDto, PorRegimeResponse, PrecificarDto, RecalculoResponse,
  ReversoDto, ReversoResponse,
} from '@triade/contracts';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ApiError, requisitar } from '@/lib/api';
import { useSessao } from '@/lib/session';
import { useConsulta, useMutacao } from './base';

/** POST /precificacao/simular: nao grava. O front so exibe o que o motor devolveu. */
export function useSimularPrecificacao() {
  return useMutacao<FormacaoCalculadaResponse, PrecificarDto>({
    metodo: 'POST', caminho: () => '/precificacao/simular', corpo: (v) => v,
  });
}

/**
 * POST /precificacao/simular como consulta (cache por parametros; nao grava). `dto` nulo = desligada.
 * Mantem o resultado anterior enquanto o novo carrega, para a tela nao piscar a cada tecla.
 */
export function useSimulacao(dto: PrecificarDto | null) {
  const { empresaId } = useSessao();
  return useQuery<FormacaoCalculadaResponse, ApiError>({
    queryKey: ['simulacao', empresaId, dto],
    queryFn: () => requisitar<FormacaoCalculadaResponse>('/precificacao/simular', { method: 'POST', body: dto }),
    enabled: dto !== null && empresaId !== null,
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 30_000,
  });
}

/**
 * POST /precificacao/reverso (nao grava): "quero X R$ de lucro / de MC" -> preco. A API calcula tudo; a tela so envia o alvo
 * e mostra o que voltou. Erros 422 (ALVO_IMPOSSIVEL, DIVISOR_NAO_POSITIVO) chegam como ApiError com a mensagem da API.
 */
export function useReverso() {
  return useMutacao<ReversoResponse, ReversoDto>({
    metodo: 'POST', caminho: () => '/precificacao/reverso', corpo: (v) => v,
  });
}

/** POST /precificacao: grava a formacao (snapshot imutavel). */
export function useGravarPrecificacao() {
  return useMutacao<FormacaoResponse, PrecificarDto>({
    metodo: 'POST', caminho: () => '/precificacao', corpo: (v) => v, invalidar: [['precificacao']],
  });
}

/** GET /precificacao/lista: sugerido, praticado e lucro simulado. Paginacao por cursor. */
export function useListaPrecos(q: Partial<ListaPrecosQuery> = {}) {
  return useConsulta<ListaPrecosResponse>(['precificacao', 'lista'], '/precificacao/lista', { query: q });
}

export function useRecalcularPrecificacao() {
  return useMutacao<RecalculoResponse, { id: string }>({
    metodo: 'POST', caminho: (v) => `/precificacao/${v.id}/recalcular`,
  });
}

/** Resposta de /precificacao/cenarios com os ids locais (formulario) de cada cenario, na mesma ordem enviada. */
export type CenariosComIds = CenariosResponse & { ids: number[] };

/**
 * POST /precificacao/cenarios como consulta (nao grava): base + variacoes de entrada calculadas pelo motor na API.
 * `dto` nulo = desligada. `ids` (ids locais dos cenarios, mesma ordem) fica na chave e volta na resposta, para a tela
 * casar cada resultado com o seu cenario mesmo se a lista mudar enquanto a resposta nova nao chega.
 */
export function useCenarios(dto: CenariosDto | null, ids: number[]) {
  const { empresaId } = useSessao();
  return useQuery<CenariosComIds, ApiError>({
    queryKey: ['cenarios', empresaId, dto, ids],
    queryFn: async () => ({ ...(await requisitar<CenariosResponse>('/precificacao/cenarios', { method: 'POST', body: dto })), ids }),
    enabled: dto !== null && empresaId !== null,
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 30_000,
  });
}

/**
 * POST /precificacao/por-regime como consulta (nao grava): o mesmo item/tabela/condicao em cada etapa da reforma.
 * `dto` nulo = desligada. Mantem o resultado anterior enquanto o novo carrega.
 */
export function usePorRegime(dto: PorRegimeDto | null) {
  const { empresaId } = useSessao();
  return useQuery<PorRegimeResponse, ApiError>({
    queryKey: ['precificacao', 'por-regime', empresaId, dto],
    queryFn: () => requisitar<PorRegimeResponse>('/precificacao/por-regime', { method: 'POST', body: dto }),
    enabled: dto !== null && empresaId !== null,
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 30_000,
  });
}

/**
 * GET /precificacao/item-custo (Sessao 37): custo do produto e composicao assim que ele e escolhido, ANTES de tabela e condicao, com as fichas do item.
 * `consulta` nula = desligada. Produto sem ficha ativa NAO e erro: volta `origem: SEM_CUSTO` com o motivo.
 */
export function useItemCusto(consulta: ItemCustoQuery | null) {
  const { empresaId } = useSessao();
  return useQuery<ItemCustoResponse, ApiError>({
    queryKey: ['precificacao', 'item-custo', empresaId, consulta],
    queryFn: () => requisitar<ItemCustoResponse>('/precificacao/item-custo', { query: { ...consulta } as never }),
    enabled: consulta !== null && empresaId !== null,
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 30_000,
  });
}
